import { apiService } from './api';

export interface CreditsData {
  userId: number;
  username: string;
  credits: number;
  memberGroupId: number;
}

export interface CreditsUpdate {
  amount: number;
  operation: 'add' | 'subtract' | 'set';
  reason?: string;
}

export interface CreditsDeduction {
  amount: number;
  clientId?: number;
  reason?: string;
}

export interface CreditsHistory {
  data: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class CreditsService {
  /**
   * Obter créditos atuais do usuário
   */
  async getCredits(): Promise<CreditsData> {
    const response = await apiService.getCredits();
    return response.data;
  }

  /**
   * Atualizar créditos do usuário
   */
  async updateCredits(update: CreditsUpdate): Promise<CreditsData> {
    const response = await apiService.updateCredits(update);
    return response.data;
  }

  /**
   * Deduzir créditos (para renovação de clientes)
   */
  async deductCredits(deduction: CreditsDeduction): Promise<CreditsData> {
    const response = await apiService.deductCredits(deduction);
    return response.data;
  }

  /**
   * Obter histórico de operações com créditos
   */
  async getCreditsHistory(page: number = 1, limit: number = 20): Promise<CreditsHistory> {
    const response = await apiService.getCreditsHistory(page, limit);
    return response;
  }

  /**
   * Adicionar créditos
   */
  async addCredits(amount: number, reason?: string): Promise<CreditsData> {
    return this.updateCredits({
      amount,
      operation: 'add',
      reason
    });
  }

  /**
   * Subtrair créditos
   */
  async subtractCredits(amount: number, reason?: string): Promise<CreditsData> {
    return this.updateCredits({
      amount,
      operation: 'subtract',
      reason
    });
  }

  /**
   * Definir créditos para um valor específico
   */
  async setCredits(amount: number, reason?: string): Promise<CreditsData> {
    return this.updateCredits({
      amount,
      operation: 'set',
      reason
    });
  }

  /**
   * Renovar cliente (deduzir créditos)
   */
  async renewClient(amount: number, clientId?: number, reason?: string): Promise<CreditsData> {
    return this.deductCredits({
      amount,
      clientId,
      reason: reason || `Renovação de cliente ${clientId || 'N/A'}`
    });
  }

  /**
   * Verificar se tem créditos suficientes
   */
  async hasEnoughCredits(requiredAmount: number): Promise<boolean> {
    const credits = await this.getCredits();
    return credits.credits >= requiredAmount;
  }

  /**
   * Obter saldo de créditos
   */
  async getBalance(): Promise<number> {
    const credits = await this.getCredits();
    return credits.credits;
  }
}

export const creditsService = new CreditsService();
